/**
 * 인증 관련 React 커스텀 훅
 */
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import * as authService from '../services/authService';

/**
 * 인증 컨텍스트
 */
const AuthContext = createContext();

/**
 * 인증 프로바이더 컴포넌트
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  // 초기 인증 상태 확인
  useEffect(() => {
    const initializeAuth = () => {
      const currentUser = authService.getCurrentUser();
      const isAuth = authService.isAuthenticated();
      
      
      if (isAuth && currentUser) {
        if(currentUser.account == 'guest') {
          authService.logout();
          setUser(null);
          navigate('/Admin', { replace: true });
          return;
        }
        setUser(currentUser);
      }
      
      setIsInitialized(true);
    };

    initializeAuth();
  }, []);

  const value = {
    user,
    setUser,
    isLoading,
    setIsLoading,
    isInitialized,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * 인증 컨텍스트 사용 훅
 */
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

/**
 * 인증 관련 주요 기능 훅
 */
export const useAuth = () => {
  const { user, setUser, isLoading, setIsLoading, isInitialized } = useAuthContext();
  const navigate = useNavigate();

  /**
   * 로그인 함수
   */
  const login = useCallback(async (userId, password, rememberMe = false) => {
    setIsLoading(true);
    
    try {
      const response = await authService.login(userId, password, rememberMe);
      if (response.success) {
        const currentUser = authService.getCurrentUser();
        setUser(currentUser);
        return { success: true, data: response, user: currentUser };
      }else{
        return { success: false, error: response.message };
      }
      
      
    } catch (error) {
      return { 
        success: false, 
        error: error.message || '로그인에 실패했습니다.' 
      };
    } finally {
      setIsLoading(false);
    }
  }, [setUser, setIsLoading]);

  /**
   * 로그아웃 함수
   */
  const logout = useCallback(async () => {
    setIsLoading(true);
    
    try {
      await authService.logout();
      
      // React 상태 초기화
      setUser(null);
      
      // 로그인 페이지로 이동
      navigate('/Admin', { replace: true });
      
    } catch (error) {
      setUser(null);
      
      // 강제로 로그인 페이지로 이동
      navigate('/Admin', { replace: true });
      
    } finally {
      setIsLoading(false);
    }
  }, [setUser, setIsLoading, navigate]);

  /**
   * 회원가입 함수
   */
  const signup = useCallback(async (signupData) => {
    setIsLoading(true);
    
    try {
      const response = await authService.signup(signupData);
      return { success: true, data: response };
    } catch (error) {
      return { 
        success: false, 
        error: error.message || '회원가입에 실패했습니다.' 
      };
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  /**
   * 토큰 유효성 검사
   */
  const validateToken = useCallback(async () => {
    try {
      const isValid = await authService.validateToken();
      if (!isValid) {
        setUser(null);
      }
      return isValid;
    } catch (error) {
      setUser(null);
      return false;
    }
  }, [setUser]);

  /**
   * 사용자 프로필 새로고침
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await authService.getUserProfile();
      if (response.user) {
        setUser(response.user);
      }
      return response;
    } catch (error) {
      throw error;
    }
  }, [setUser]);

  return {
    // 상태
    user,
    isLoading,
    isInitialized,
    isAuthenticated: !!user,
    
    // 함수
    login,
    logout,
    signup,
    validateToken,
    refreshUser,
  };
};

/**
 * 로그인이 필요한 페이지를 위한 훅
 */
export const useRequireAuth = (redirectTo = '/Admin') => {
  const { isAuthenticated, isInitialized, validateToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      // 초기화가 완료될 때까지 대기
      if (!isInitialized) return;
      if (!isAuthenticated) {
        navigate(redirectTo, { replace: true });
        return;
      }
      
      // 토큰 유효성 검사
      const isValid = await validateToken();
      if (!isValid) {
        navigate(redirectTo, { replace: true });
      }
    };

    checkAuth();
  }, [isAuthenticated, isInitialized, validateToken, navigate, redirectTo]);

  return { 
    isAuthenticated, 
    isInitialized,
    isLoading: !isInitialized 
  };
};

/**
 * 로그인된 사용자만 접근 가능한 컴포넌트
 */
export const ProtectedRoute = ({ children, fallback = null, redirectTo = '/Admin' }) => {
  const { isAuthenticated, isLoading } = useRequireAuth(redirectTo);

  if (isLoading) {
    return fallback || <div>로딩 중...</div>;
  }

  return isAuthenticated ? children : null;
};